import React, { useEffect } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { RootState, AppDispatch } from '@/store/store'
import { setMobileMenuOpen } from '@/store/slices/uiSlice'
import { logoutUser } from '@/store/slices/authSlice'
import { fetchGigs, fetchFeaturedGigs, fetchUserGigs } from '@/store/slices/gigsSlice'
import { fetchApplicationsCount } from '@/store/slices/applicationsSlice'
import { fetchUserFavoritesCount } from '@/store/slices/likesSlice'
import {
    FiHome,
    FiSearch,
    FiBriefcase,
    FiMessageCircle,
    FiUser,
    FiSettings,
    FiPlus,
    FiFileText,
    FiStar,
    FiTrendingUp,
    FiLogOut,
    FiX,
    FiShield
} from 'react-icons/fi'

// Hide scrollbar while keeping scroll functionality
const scrollbarHideStyle = `
    .nav-scrollable::-webkit-scrollbar {
        display: none;
    }

    .nav-scrollable {
        scrollbar-width: none;
        -ms-overflow-style: none;
        overscroll-behavior: contain;
    }
`;

interface SidebarItemProps {
    icon: React.ReactNode
    label: string
    path: string
    badge?: number
    isActive?: boolean
    onClick?: () => void
}

const SidebarItem: React.FC<SidebarItemProps> = ({
    icon,
    label,
    path,
    badge,
    isActive,
    onClick
}) => {
    return (
        <Link
            to={path}
            onClick={onClick}
            className={`
        flex items-center px-4 py-3 text-sm font-medium rounded-lg mx-2 transition-colors duration-200
        ${isActive
                    ? 'bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-300'
                    : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
                }
      `}
        >
            <span className="mr-3">{icon}</span>
            <span className="flex-1">{label}</span>
            {badge && badge > 0 && (
                <span className="bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                    {badge > 9 ? '9+' : badge}
                </span>
            )}
        </Link>
    )
}

const Sidebar: React.FC = () => {
    const location = useLocation()
    const navigate = useNavigate()
    const dispatch = useDispatch<AppDispatch>()
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { sidebarOpen: _sidebarOpen, mobileMenuOpen } = useSelector((state: RootState) => state.ui)
    const { user, isAuthenticated } = useSelector((state: RootState) => state.auth)
    const { unreadCount } = useSelector((state: RootState) => state.messages)
    const { totalCount: gigsCount, featuredCount, userGigsTotalCount } = useSelector((state: RootState) => state.gigs)
    const { totalCount: applicationsCount } = useSelector((state: RootState) => state.applications)
    const { userFavoritesCount } = useSelector((state: RootState) => state.likes)



    const handleCloseSidebar = () => {
        // Only close mobile menu, desktop sidebar stays open
        dispatch(setMobileMenuOpen(false))
    }

    const handleSignOut = async () => {
        try {
            await dispatch(logoutUser()).unwrap()
            handleCloseSidebar()
            navigate('/')
        } catch (error) {
            console.error('Sign out error:', error)
        }
    }

    // Fetch public counts (always fetch these regardless of auth status)
    useEffect(() => {
        // Fetch gigs count - public data
        dispatch(fetchGigs({ page: 1, limit: 1 })) // Just fetch 1 to get total count
        // Fetch featured gigs count - public data
        dispatch(fetchFeaturedGigs())
    }, [dispatch])

    // Fetch user-specific counts when authenticated
    useEffect(() => {
        if (isAuthenticated && user) {
            // Fetch user-specific counts
            dispatch(fetchApplicationsCount(user.role as 'worker' | 'employer'))
            dispatch(fetchUserFavoritesCount())

            // Fetch user's gigs count if employer
            if (user.role === 'employer') {
                dispatch(fetchUserGigs({ page: 1, limit: 1 })) // Just fetch 1 to get total count
            }
        }
    }, [isAuthenticated, user, dispatch])

    const publicItems = [
        { icon: <FiHome className="w-5 h-5" />, label: 'Home', path: '/' },
        { icon: <FiSearch className="w-5 h-5" />, label: 'Browse Gigs', path: '/gigs', badge: gigsCount || 0 },
        { icon: <FiTrendingUp className="w-5 h-5" />, label: 'Featured', path: '/gigs?featured=true', badge: featuredCount || 0 },
    ]

    const userItems = [
        { icon: <FiUser className="w-5 h-5" />, label: 'Dashboard', path: '/dashboard' },
        {
            icon: <FiMessageCircle className="w-5 h-5" />,
            label: 'Messages',
            path: '/dashboard/messages',
            badge: isAuthenticated && user ? unreadCount : 0
        },
        {
            icon: <FiFileText className="w-5 h-5" />,
            label: 'Applications',
            path: '/dashboard/applications',
            badge: isAuthenticated && user ? applicationsCount : 0
        },
        {
            icon: <FiStar className="w-5 h-5" />,
            label: 'Favorites',
            path: '/dashboard/favorites',
            badge: isAuthenticated && user ? userFavoritesCount : 0
        },
    ]

    const employerItems = [
        { icon: <FiPlus className="w-5 h-5" />, label: 'Post a Gig', path: '/dashboard/gigs/create' },
        {
            icon: <FiBriefcase className="w-5 h-5" />,
            label: 'My Gigs',
            path: '/dashboard/my-gigs',
            badge: isAuthenticated && user?.role === 'employer' ? userGigsTotalCount : 0
        },
    ]

    const settingsItems = [
        { icon: <FiUser className="w-5 h-5" />, label: 'Profile', path: '/dashboard/profile' },
        { icon: <FiShield className="w-5 h-5" />, label: 'Verification', path: '/dashboard/verification' },
        { icon: <FiSettings className="w-5 h-5" />, label: 'Settings', path: '/dashboard/settings' },
    ]

    return (
        <>
            <style>{scrollbarHideStyle}</style>
            
            {/* Mobile overlay */}
            {mobileMenuOpen && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
                    onClick={handleCloseSidebar}
                />
            )}

            {/* Sidebar */}
            <aside
                className={`
          fixed inset-y-0 left-0 z-50 w-64 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 transform transition-transform duration-300 ease-in-out
          ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
          lg:relative lg:transform-none lg:translate-x-0
        `}
            >
                {/* Close button for mobile/tablet menu */}
                <button
                    onClick={handleCloseSidebar}
                    className="absolute top-3 right-3 lg:hidden p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 z-10"
                    aria-label="Close sidebar"
                >
                    <FiX className="w-5 h-5" />
                </button>

                <div className="flex flex-col h-full">
                    {/* Navigation */}
                    <nav className="nav-scrollable flex-1 px-2 py-6 space-y-2 overflow-y-auto">
                        {/* Public Navigation */}
                        <div>
                            <h3 className="px-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                                Explore
                            </h3>
                            {publicItems.map((item) => (
                                <SidebarItem
                                    key={item.path}
                                    icon={item.icon}
                                    label={item.label}
                                    path={item.path}
                                    badge={item.badge}
                                    isActive={location.pathname === item.path}
                                    onClick={handleCloseSidebar}
                                />
                            ))}
                        </div>

                        {/* User Navigation */}
                        {isAuthenticated && (
                            <>
                                <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                                    <h3 className="px-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                                        Your Account
                                    </h3>
                                    {userItems.map((item) => (
                                        <SidebarItem
                                            key={item.path}
                                            icon={item.icon}
                                            label={item.label}
                                            path={item.path}
                                            badge={item.badge}
                                            isActive={location.pathname === item.path}
                                            onClick={handleCloseSidebar}
                                        />
                                    ))}
                                </div>

                                {/* Employer-specific items */}
                                {user?.role === 'employer' && (
                                    <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                                        <h3 className="px-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                                            Employer Tools
                                        </h3>
                                        {employerItems.map((item) => (
                                            <SidebarItem
                                                key={item.path}
                                                icon={item.icon}
                                                label={item.label}
                                                path={item.path}
                                                badge={item.badge}
                                                isActive={location.pathname === item.path}
                                                onClick={handleCloseSidebar}
                                            />
                                        ))}
                                    </div>
                                )}

                                {/* Settings */}
                                <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                                    <h3 className="px-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                                        Settings
                                    </h3>
                                    {settingsItems.map((item) => (
                                        <SidebarItem
                                            key={item.path}
                                            icon={item.icon}
                                            label={item.label}
                                            path={item.path}
                                            isActive={location.pathname === item.path}
                                            onClick={handleCloseSidebar}
                                        />
                                    ))}

                                    {/* Sign Out Button */}
                                    <button
                                        onClick={handleSignOut}
                                        className="flex items-center px-4 py-3 text-sm font-medium rounded-lg mx-2 transition-colors duration-200 text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20 w-full"
                                    >
                                        <span className="mr-3">
                                            <FiLogOut className="w-5 h-5" />
                                        </span>
                                        <span className="flex-1 text-left">Sign Out</span>
                                    </button>
                                </div>
                            </>
                        )}
                    </nav>

                    {/* Footer */}
                    <div className="p-4 border-t border-gray-200 dark:border-gray-700">
                        <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
                            © {new Date().getFullYear()} SideGO
                        </p>
                    </div>
                </div>
            </aside>
        </>
    )
}

export default Sidebar